import { useState, useEffect } from 'react'
import { Cog, Clock, MessageSquare, Gauge, Save, RotateCcw } from 'lucide-react'
import {
  useWaSettings,
  useUpdateWaSettings,
  useResetWaSettings,
  useWaBusinessHoursCheck,
  useWaRateLimitCheck,
} from '@hooks/useWaSettings'
import { cn } from '@lib/utils'

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
]

export function WaSettingsPage() {
  const { data: settings, isLoading } = useWaSettings()
  const { data: businessHoursStatus } = useWaBusinessHoursCheck()
  const { data: rateLimitStatus } = useWaRateLimitCheck()

  const updateSettings = useUpdateWaSettings()
  const resetSettings = useResetWaSettings()

  const [formData, setFormData] = useState({
    timezone: 'UTC',
    autoReplyEnabled: false,
    autoReplyMessage: '',
    businessHoursOnly: false,
    businessHoursStart: 9,
    businessHoursEnd: 17,
    businessDays: [1, 2, 3, 4, 5],
    rateLimitEnabled: true,
    messagesPerMinute: 30,
    messagesPerHour: 1000,
    messagesPerDay: 10000,
  })

  useEffect(() => {
    if (settings) {
      setFormData({
        timezone: settings.timezone || 'UTC',
        autoReplyEnabled: settings.autoReplyEnabled || false,
        autoReplyMessage: settings.autoReplyMessage || '',
        businessHoursOnly: settings.businessHoursOnly || false,
        businessHoursStart: settings.businessHoursStart || 9,
        businessHoursEnd: settings.businessHoursEnd || 17,
        businessDays: settings.businessDays || [1, 2, 3, 4, 5],
        rateLimitEnabled: settings.rateLimitConfig?.enabled ?? true,
        messagesPerMinute: settings.rateLimitConfig?.messagesPerMinute || 30,
        messagesPerHour: settings.rateLimitConfig?.messagesPerHour || 1000,
        messagesPerDay: settings.rateLimitConfig?.messagesPerDay || 10000,
      })
    }
  }, [settings])

  const handleSave = async () => {
    await updateSettings.mutateAsync({
      timezone: formData.timezone,
      autoReplyEnabled: formData.autoReplyEnabled,
      autoReplyMessage: formData.autoReplyMessage || null,
      businessHoursOnly: formData.businessHoursOnly,
      businessHoursStart: formData.businessHoursStart,
      businessHoursEnd: formData.businessHoursEnd,
      businessDays: formData.businessDays,
      rateLimitConfig: {
        enabled: formData.rateLimitEnabled,
        messagesPerMinute: formData.messagesPerMinute,
        messagesPerHour: formData.messagesPerHour,
        messagesPerDay: formData.messagesPerDay,
      },
    })
  }

  const handleReset = async () => {
    if (confirm('Are you sure you want to reset all settings to default?')) {
      await resetSettings.mutateAsync()
    }
  }

  const toggleDay = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      businessDays: prev.businessDays.includes(day)
        ? prev.businessDays.filter((d) => d !== day)
        : [...prev.businessDays, day].sort(),
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Cog size={32} className="text-green-600" />
          <h1 className="text-3xl font-bold text-gray-900">WhatsApp Settings</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            disabled={resetSettings.isPending}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RotateCcw size={18} />
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            disabled={updateSettings.isPending}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            <Save size={18} />
            Save Changes
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 gap-4">
        {businessHoursStatus && (
          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className="text-gray-500" />
              <span className="font-medium">Business Hours</span>
            </div>
            <div
              className={cn(
                'text-sm',
                businessHoursStatus.isWithinBusinessHours ? 'text-green-600' : 'text-orange-600'
              )}
            >
              {businessHoursStatus.isWithinBusinessHours
                ? 'Currently within business hours'
                : `Outside business hours. Next: ${businessHoursStatus.nextBusinessHoursStart || 'N/A'}`}
            </div>
          </div>
        )}

        {rateLimitStatus && (
          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Gauge size={18} className="text-gray-500" />
              <span className="font-medium">Rate Limit Status</span>
            </div>
            <div
              className={cn(
                'text-sm',
                rateLimitStatus.isLimited ? 'text-red-600' : 'text-green-600'
              )}
            >
              {rateLimitStatus.isLimited
                ? `Limited - Reset in ${rateLimitStatus.resetInSeconds}s`
                : `OK - ${rateLimitStatus.remainingMessages} messages remaining`}
            </div>
          </div>
        )}
      </div>

      {/* Auto-Reply Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare size={20} className="text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Auto-Reply</h2>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.autoReplyEnabled}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, autoReplyEnabled: e.target.checked }))
              }
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-5 w-5"
            />
            <span className="font-medium text-gray-700">Enable auto-reply</span>
          </label>

          {formData.autoReplyEnabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auto-reply message
              </label>
              <textarea
                value={formData.autoReplyMessage}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, autoReplyMessage: e.target.value }))
                }
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Enter your auto-reply message..."
              />
            </div>
          )}
        </div>
      </div>

      {/* Business Hours Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={20} className="text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Business Hours</h2>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.businessHoursOnly}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, businessHoursOnly: e.target.checked }))
              }
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-5 w-5"
            />
            <span className="font-medium text-gray-700">Only send messages during business hours</span>
          </label>

          {formData.businessHoursOnly && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start hour
                  </label>
                  <select
                    value={formData.businessHoursStart}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        businessHoursStart: parseInt(e.target.value),
                      }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i.toString().padStart(2, '0')}:00
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End hour</label>
                  <select
                    value={formData.businessHoursEnd}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        businessHoursEnd: parseInt(e.target.value),
                      }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i.toString().padStart(2, '0')}:00
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business days
                </label>
                <div className="flex gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day.value}
                      onClick={() => toggleDay(day.value)}
                      className={cn(
                        'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        formData.businessDays.includes(day.value)
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <select
                  value={formData.timezone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, timezone: e.target.value }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New_York (EST)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                  <option value="Europe/London">Europe/London (GMT)</option>
                  <option value="Europe/Paris">Europe/Paris (CET)</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                  <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rate Limiting Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Gauge size={20} className="text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Rate Limiting</h2>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.rateLimitEnabled}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, rateLimitEnabled: e.target.checked }))
              }
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-5 w-5"
            />
            <span className="font-medium text-gray-700">Enable rate limiting</span>
          </label>

          {formData.rateLimitEnabled && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Messages per minute
                  </label>
                  <input
                    type="number"
                    value={formData.messagesPerMinute}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        messagesPerMinute: parseInt(e.target.value) || 0,
                      }))
                    }
                    min={1}
                    max={100}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Messages per hour
                  </label>
                  <input
                    type="number"
                    value={formData.messagesPerHour}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        messagesPerHour: parseInt(e.target.value) || 0,
                      }))
                    }
                    min={1}
                    max={5000}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Messages per day
                  </label>
                  <input
                    type="number"
                    value={formData.messagesPerDay}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        messagesPerDay: parseInt(e.target.value) || 0,
                      }))
                    }
                    min={1}
                    max={100000}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Rate Limiting Best Practices */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <h4 className="text-sm font-medium text-amber-800 mb-2">Recommended Rate Limits</h4>
                <ul className="text-xs text-amber-700 space-y-1.5 list-disc list-inside">
                  <li><strong>New accounts:</strong> Start with 10-20 messages/min, 200-500/hour to build trust</li>
                  <li><strong>Established accounts:</strong> 30-50 messages/min, 1000-2000/hour is safe</li>
                  <li><strong>WhatsApp Business API:</strong> Supports up to 80 messages/second (tier dependent)</li>
                  <li><strong>Avoid spikes:</strong> Gradual ramp-up prevents flagging; increase limits by 20% weekly</li>
                  <li><strong>Quality over quantity:</strong> High reply rates improve your messaging tier faster</li>
                  <li><strong>Daily cap:</strong> Keep under 10,000/day unless you have enterprise tier approval</li>
                </ul>
                <p className="text-xs text-amber-600 mt-2 italic">
                  Exceeding WhatsApp&apos;s limits may result in temporary or permanent account restrictions.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
